import { message } from 'antd';
import type { AxiosResponse } from 'axios';
import {
  call,
  put,
  takeEvery,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import type { ActionType } from 'typesafe-actions';

import { client } from '@portal/api-client/src/client';
import type { LeadHydrationConfig } from '@portal/api-models';

import {
  createCreditCardLeadHydrationConfigAction,
  createLeadHydrationConfigAction,
  deleteCreditCardLeadHydrationConfigAction,
  getLeadHydrationConfigsBySubAccountUuidAction,
} from './actions';

export function* getLeadHydrationConfigBySubAccountUuid({
  payload: { subAccountUuid },
}: ActionType<typeof getLeadHydrationConfigsBySubAccountUuidAction.request>) {
  try {
    const { data: response }: AxiosResponse<LeadHydrationConfig[]> = yield call(
      [client, 'get'],
      `/third-party-data/lead-hydration/${subAccountUuid}`
    );

    yield put(
      getLeadHydrationConfigsBySubAccountUuidAction.success({
        response,
        subAccountUuid,
      })
    );
  } catch (err: any) {
    yield put(getLeadHydrationConfigsBySubAccountUuidAction.failure(err));
  }
}

export function* createLeadHydrationConfig({
  payload,
}: ActionType<typeof createLeadHydrationConfigAction.request>) {
  try {
    const { data: newLeadHydrationConfig }: AxiosResponse<LeadHydrationConfig> =
      yield call(
        [client, 'post'],
        '/third-party-data/lead-hydration/',
        payload
      );

    yield put(createLeadHydrationConfigAction.success(newLeadHydrationConfig));

    yield put(
      getLeadHydrationConfigsBySubAccountUuidAction.request({
        subAccountUuid: payload.subAccountUuid || '',
      })
    );
  } catch (err: any) {
    yield put(createLeadHydrationConfigAction.failure(err));
  }
}

export function* createCreditCardLeadHydrationConfig({
  payload,
}: ActionType<typeof createCreditCardLeadHydrationConfigAction.request>) {
  try {
    const {
      data: newLeadHydrationConfigCC,
    }: AxiosResponse<LeadHydrationConfig> = yield call(
      [client, 'post'],
      '/third-party-data/credit-card-lead-hydration/',
      payload
    );

    yield put(
      createCreditCardLeadHydrationConfigAction.success(
        newLeadHydrationConfigCC
      )
    );
  } catch (err: any) {
    yield put(createCreditCardLeadHydrationConfigAction.failure(err));
  }
}

export function* deleteCreditCardLeadHydrationConfig({
  payload: { id, subAccountUuid },
}: ActionType<typeof deleteCreditCardLeadHydrationConfigAction.request>) {
  try {
    yield call(
      [client, 'delete'],
      `/third-party-data/credit-card-lead-hydration/${id}`
    );

    yield put(
      getLeadHydrationConfigsBySubAccountUuidAction.request({
        subAccountUuid,
      })
    );

    yield call(
      message.success,
      'The credit card lead hydration configuration was deleted'
    );
  } catch (err: any) {
    yield put(deleteCreditCardLeadHydrationConfigAction.failure(err));
  }
}

export function* leadHydrationSaga() {
  yield takeLatest(
    getType(getLeadHydrationConfigsBySubAccountUuidAction.request),
    getLeadHydrationConfigBySubAccountUuid
  );
  yield takeEvery(
    getType(createLeadHydrationConfigAction.request),
    createLeadHydrationConfig
  );
  yield takeEvery(
    getType(createCreditCardLeadHydrationConfigAction.request),
    createCreditCardLeadHydrationConfig
  );
  yield takeLeading(
    getType(deleteCreditCardLeadHydrationConfigAction.request),
    deleteCreditCardLeadHydrationConfig
  );
}
